import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EffectRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { EmployeeModel, EmployeeRoleModel } from '@libs/account/models';
import { ButtonComponent } from '@libs/common/components';
import { SelectionListComponent } from '@libs/form/components';
import { CheckboxesInputModel, SelectionListValue } from '@libs/form/models';
import { CheckboxesOptionModel } from '@libs/shared/models';

interface EmployeeEditFormValue {
  employee: EmployeeModel | undefined;
  assignedRoles: SelectionListValue;
}

@Component({
  selector: 'ui-employee-edit',
  templateUrl: './employee-edit.component.html',
  styleUrls: ['./employee-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectionListComponent, ReactiveFormsModule, ButtonComponent],
})
export class EmployeeEditComponent {
  private readonly syncRolesWithInput: EffectRef = effect(() => {
    this.rolesControl.setValue(this.mapAssignedRolesToSelection(this.employee()), { emitEvent: false });
  });

  public readonly employee: InputSignal<EmployeeModel | undefined> = input<EmployeeModel | undefined>();

  public readonly rolesData: Signal<CheckboxesInputModel> = computed(() => ({
    type: 'checkboxes',
    name: 'roles',
    label: 'Berechtigungen',
    options: (this.employee()?.assignableRoles ?? []).map((role: EmployeeRoleModel): CheckboxesOptionModel => ({
      name: String(role.id),
      label: role.displayName,
    })),
  }));

  public readonly submitResult: OutputEmitterRef<EmployeeEditFormValue> = output<EmployeeEditFormValue>();
  public readonly cancelClicked: OutputEmitterRef<void> = output<void>();

  public rolesControl: FormControl<SelectionListValue> = new FormControl<SelectionListValue>({});

  public onSubmit(event?: Event): void {
    event?.preventDefault();

    this.submitResult.emit({
      employee: this.employee(),
      assignedRoles: this.rolesControl.value,
    });
  }

  public onCancel(): void {
    this.cancelClicked.emit();
  }

  private mapAssignedRolesToSelection(employee: EmployeeModel | undefined): SelectionListValue {
    if (!employee) {
      return {};
    }

    const assignableRoles: EmployeeRoleModel[] = employee.assignableRoles ?? [];
    const assignedRoleIds: Set<number> = new Set(
      (employee.assignedRoles ?? []).map((assignedRole: EmployeeRoleModel) => assignedRole.id),
    );

    return Object.fromEntries(
      assignableRoles.map((role: EmployeeRoleModel): [string, boolean] =>
        [
          String(role.id),
          assignedRoleIds.has(role.id),
        ],
    ));
  }
}
